import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const About = () => {
  const { elementRef, isVisible } = useScrollAnimation();

  return (
    <section id="about" className="py-20 bg-gradient-to-br from-amber-50 to-yellow-50 relative overflow-hidden">
      {/* Background texture */}
      <div className="absolute inset-0 bg-pattern opacity-30"></div>
      
      <div className="relative z-10 max-w-6xl mx-auto px-6">
        <div ref={elementRef} className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'animate-fade-in-up' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-4xl md:text-6xl font-bold mb-6 gradient-earth font-serif">About Me</h2>
          <div className="w-24 h-1 bg-gradient-to-r from-amber-600 to-yellow-600 mx-auto rounded-full"></div>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Enhanced Image Section - Made Bigger */}
          <div className={`transition-all duration-1200 ${isVisible ? 'animate-slide-in-left' : 'opacity-0 -translate-x-8'}`}>
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-400/20 to-yellow-500/20 rounded-3xl transform rotate-3 group-hover:rotate-6 transition-transform duration-500"></div>
              <div className="relative rounded-3xl shadow-2xl transform group-hover:-rotate-1 transition-all duration-500 hover-lift">
                <img
                  src="https://static.readdy.ai/image/ece5776bbcd9be3dff5ef9cd5ed3458d/10fdb0d4139abfaa7e6e1d71990e4e31.jpeg"
                  alt="Afiki Rajabo Chiwaya - Agricultural Professional"
                  className="w-full h-[500px] object-contain rounded-3xl"
                />
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-t from-amber-900/5 to-transparent"></div>
              </div>
              
              {/* Floating decorative elements */}
              <div className="absolute -top-4 -right-4 w-8 h-8 bg-yellow-400 rounded-full animate-float opacity-70"></div>
              <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-amber-500 rounded-full animate-float opacity-60" style={{ animationDelay: '1s' }}></div>
            </div>
          </div>

          {/* Enhanced Content Section */}
          <div className={`transition-all duration-1200 ${isVisible ? 'animate-slide-in-right' : 'opacity-0 translate-x-8'}`}>
            <div className="space-y-6">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl hover-earth card-hover">
                <h3 className="text-2xl font-bold text-amber-900 mb-4 font-serif">Professional Background</h3>
                <p className="text-gray-700 leading-relaxed text-lg">
                  I am an ambitious and detail-oriented agricultural graduate with over 5 years of administrative experience. 
                  My passion lies in sustainable farming practices and agricultural innovation that can transform communities.
                </p>
              </div>

              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl hover-earth card-hover">
                <h3 className="text-2xl font-bold text-amber-900 mb-4 font-serif">Core Values</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-green-600 rounded-full animate-pulse"></div>
                    <span className="text-gray-700 font-medium">Sustainability</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-amber-600 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
                    <span className="text-gray-700 font-medium">Innovation</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-yellow-600 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
                    <span className="text-gray-700 font-medium">Excellence</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-green-700 rounded-full animate-pulse" style={{ animationDelay: '1.5s' }}></div>
                    <span className="text-gray-700 font-medium">Community</span>
                  </div>
                </div>
              </div>

              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl hover-earth card-hover">
                <h3 className="text-2xl font-bold text-amber-900 mb-4 font-serif">Mission</h3>
                <p className="text-gray-700 leading-relaxed text-lg">
                  To contribute to sustainable agricultural development through innovative farming techniques, 
                  efficient project coordination, and community-focused agricultural solutions.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Stats Section */}
        <div className={`mt-20 transition-all duration-1200 ${isVisible ? 'animate-fade-in-up' : 'opacity-0 translate-y-8'}`}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: '5+', label: 'Years Experience', icon: 'ri-time-line', color: 'from-amber-400 to-amber-600' },
              { number: '50+', label: 'Projects Completed', icon: 'ri-plant-line', color: 'from-green-400 to-green-600' },
              { number: '100%', label: 'Commitment', icon: 'ri-heart-line', color: 'from-yellow-400 to-yellow-600' },
              { number: '24/7', label: 'Dedication', icon: 'ri-star-line', color: 'from-orange-400 to-orange-600' }
            ].map((stat, index) => (
              <div 
                key={index} 
                className="text-center bg-white/60 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover-lift card-hover"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className={`w-16 h-16 mx-auto mb-4 bg-gradient-to-br ${stat.color} rounded-full flex items-center justify-center text-white text-2xl animate-float`}>
                  <i className={stat.icon}></i>
                </div>
                <div className={`text-3xl font-bold gradient-earth mb-2`}>{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
